import { Link, useLocation } from "react-router-dom";

import type { PublishedNavigationElement } from "@/lib/api/types";
import { useMaritimeWarningColor } from "@/features/dashboard/components/warning-tool/use-maritime-warning-color";
import { useDashboardWarningSettings } from "@/features/shared/hooks";
import { useWarningPageNavLabel } from "@/features/shared/hooks/use-warning-page-defaults";
import { useTranslation } from "@/lib/i18n";
import { hasMaritimeWarningsEnabled } from "@/lib/maritime-warning-page-settings.utils";
import { navigationElementIcon } from "@/lib/navigation-element-kinds";

import NavigationItem from "./navigation-item";

/**
 * `maritime_warnings_page` navigation element: CMS owns label and position. Shown only
 * with the maritime switch on and at least one domain, like the risk page follows its
 * own switch. Like the land link, a colored dot replaces the icon while a warning is active.
 */
export function NavigationWarningsPageLink({
  element,
}: {
  element: PublishedNavigationElement;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navLabel = useWarningPageNavLabel();
  const { selectedDashboard, maritimeWarningPage } = useDashboardWarningSettings();
  // Same source as the toolbar button: undefined while loading, on error or on green.
  const color = useMaritimeWarningColor();

  if (!selectedDashboard || !hasMaritimeWarningsEnabled(maritimeWarningPage)) return null;
  if (!element.full_path) return null;

  return (
    <Link to={element.full_path} tabIndex={0}>
      <NavigationItem
        icon={navigationElementIcon(element)}
        active={location.pathname === element.full_path}
        dot={
          color && {
            color: `var(--${color})`,
            label: String(
              t("dashboard.warnings.maritime.navigationDot", {
                level: String(t(`code.${color}`)),
              }),
            ),
          }
        }
      >
        {navLabel(element)}
      </NavigationItem>
    </Link>
  );
}
